import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Store, ArrowLeft } from 'lucide-react';
import { useFeaturedStores } from '../../hooks/useStoreApi';

const AUTO_SCROLL_PX_PER_SEC = 28;

const StoreCard = ({ store }) => {
  const handleClick = () => {
    if (!store?.store_slug) return;
    window.location.assign(`/${store.store_slug}`);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group relative shrink-0 w-60 sm:w-64 md:w-72 rounded-3xl overflow-hidden bg-white shadow-elevated hover:shadow-2xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2"
      aria-label={store?.name ? `פתיחת חנות ${store.name}` : 'פתיחת חנות'}
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-gradient-to-br from-secondary-100 to-secondary-200">
        {store?.cover_image_url ? (
          <img
            src={store.cover_image_url}
            alt={store.name || 'חנות'}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-smooth group-hover:scale-105"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Store className="w-14 h-14 text-secondary-500" strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

        {/* Profile badge */}
        {store?.owner_profile_url && (
          <div className="absolute top-3 right-3 w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/90 shadow-lg bg-white">
            <img
              src={store.owner_profile_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
          </div>
        )}

        {/* Content */}
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 text-right" dir="rtl">
          <h3
            className="text-white text-lg sm:text-xl font-black leading-tight mb-1 line-clamp-2"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}
          >
            {store?.name || 'חנות מקומית'}
          </h3>
          {store?.about && (
            <p
              className="text-white/85 text-xs sm:text-sm leading-snug line-clamp-2"
              style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}
            >
              {store.about.replace(/[\n\r]+/g, ' ').slice(0, 90)}
            </p>
          )}
          <div className="mt-2 inline-flex items-center gap-1 text-secondary-300 text-xs sm:text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
            <span>לחנות</span>
            <ArrowLeft className="w-4 h-4" />
          </div>
        </div>
      </div>
    </button>
  );
};

const SkeletonCard = () => (
  <div className="shrink-0 w-60 sm:w-64 md:w-72 rounded-3xl overflow-hidden bg-white shadow-elevated">
    <div className="aspect-[4/5] w-full bg-secondary-100/60 animate-pulse" />
  </div>
);

export const TopStoresShowcase = () => {
  const { stores, loading, error } = useFeaturedStores(12);
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });
  const [paused, setPaused] = useState(false);

  // Duplicate the list so the track can loop seamlessly.
  const loopStores = stores.length > 0 ? [...stores, ...stores] : [];

  useEffect(() => {
    if (loading || error || stores.length === 0) return;
    const track = trackRef.current;
    if (!track) return;

    let raf = 0;
    let last = performance.now();
    let offset = 0;
    let halfWidth = 0;

    const measure = () => {
      halfWidth = track.scrollWidth / 2;
    };
    measure();

    const step = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      if (!paused && halfWidth > 0) {
        offset += AUTO_SCROLL_PX_PER_SEC * dt;
        if (offset >= halfWidth) offset -= halfWidth;
        // RTL: scroll in the opposite direction so the row drifts right-to-left visually
        track.style.transform = `translate3d(${offset}px, 0, 0)`;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const ro = new ResizeObserver(measure);
    ro.observe(track);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [loading, error, stores.length, paused]);

  if (error || (!loading && stores.length === 0)) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="relative py-12 sm:py-16 overflow-hidden bg-gradient-to-br from-brand-700 via-brand-600 to-brand-700"
      dir="rtl"
    >
      {/* Soft decorative blobs */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-secondary-500/30 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-brand-400/30 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-8 sm:mb-10"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight mb-2">
            חנויות שהקהילה יצרה
          </h2>
          <p className="text-secondary-200 text-base sm:text-lg md:text-xl font-medium">
            תציצו במגוון החנויות שנפתחו על ידי חברי הקהילה
          </p>
        </motion.div>
      </div>

      {/* Carousel */}
      <div
        className="relative"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        {/* Edge fades */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-brand-700 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-brand-700 to-transparent z-10" />

        <div className="overflow-hidden">
          <div
            ref={trackRef}
            className="flex gap-4 sm:gap-5 px-4 sm:px-6 will-change-transform"
            style={{ width: 'max-content' }}
          >
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={`sk-${i}`} />)
              : loopStores.map((store, i) => (
                  <StoreCard key={`${store.store_id || store.store_slug}-${i}`} store={store} />
                ))}
          </div>
        </div>
      </div>
    </section>
  );
};
